// TOC (Table of Contents): h1/h2/h3 из markdown, click-to-scroll, scrollspy.
//
// Лёгкая реализация без MutationObserver:
// - parseHeadings → список TOC из markdown (через useMemo)
// - On click: querySelectorAll по DOM, поиск по textContent, scrollIntoView
// - Scrollspy: IntersectionObserver, пересоздаётся когда меняется markdown
//   (markdown throttled родителем до 600ms, поэтому не часто)

import { useEffect, useMemo, useRef, useState } from 'react';

interface TocItem {
  level: 1 | 2 | 3;
  text: string;
  slug: string;
}

interface DocumentTocProps {
  markdown: string;
  containerSelector: string;
}

const SLUG_RE = /[^\p{L}\p{N}\-_\s]/gu;
function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(SLUG_RE, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);
}

function parseHeadings(md: string): TocItem[] {
  const out: TocItem[] = [];
  const seen = new Map<string, number>();
  const lines = md.split('\n');
  let inFence = false;
  let fenceMarker = '';
  for (const raw of lines) {
    const line = raw.trimEnd();
    const fenceMatch = /^(```|~~~)/.exec(line);
    if (fenceMatch) {
      if (!inFence) {
        inFence = true;
        fenceMarker = fenceMatch[1];
      } else if (line.startsWith(fenceMarker)) {
        inFence = false;
      }
      continue;
    }
    if (inFence) continue;
    const m = /^(#{1,3})\s+(.+?)\s*#*$/.exec(line);
    if (!m) continue;
    const level = m[1].length as 1 | 2 | 3;
    const text = m[2]
      .replace(/`/g, '')
      .replace(/\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g, (_, t, a) => a ?? t)
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/__(.+?)__/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/_(.+?)_/g, '$1');
    const baseSlug = slugify(text);
    if (!baseSlug) continue;
    const dup = seen.get(baseSlug) ?? 0;
    const slug = dup > 0 ? `${baseSlug}-${dup}` : baseSlug;
    seen.set(baseSlug, dup + 1);
    out.push({ level, text, slug });
  }
  return out;
}

export function DocumentToc({ markdown, containerSelector }: DocumentTocProps) {
  const items = useMemo(() => parseHeadings(markdown), [markdown]);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Scrollspy: пересоздаём IntersectionObserver когда меняется markdown.
  // markdown приходит от родителя throttled на 600ms, поэтому пересоздание
  // происходит редко (не на каждый keystroke).
  useEffect(() => {
    const container = document.querySelector(containerSelector);
    if (!container) return;
    const root = container.querySelector('.ProseMirror');
    if (!root) return;

    const heads = root.querySelectorAll<HTMLElement>('h1, h2, h3');
    if (heads.length === 0) return;

    const scrollParent = (container.closest('.overflow-auto') as Element | null) ?? container;
    const slugByElement = new WeakMap<HTMLElement, string>();
    const tocSlugs = new Set(itemsRef.current.map((it) => it.slug));

    // Сопоставляем DOM-заголовки со слагами из TOC по textContent
    const seen = new Map<string, number>();
    heads.forEach((el) => {
      const txt = (el.textContent ?? '').trim();
      const baseSlug = slugify(txt);
      if (!baseSlug) return;
      const dup = seen.get(baseSlug) ?? 0;
      const slug = dup > 0 ? `${baseSlug}-${dup}` : baseSlug;
      seen.set(baseSlug, dup + 1);
      if (tocSlugs.has(slug)) slugByElement.set(el, slug);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .map((e) => ({
            el: e.target as HTMLElement,
            top: e.boundingClientRect.top,
          }))
          .sort((a, b) => a.top - b.top);
        if (visible.length > 0) {
          const slug = slugByElement.get(visible[0].el);
          if (slug) setActiveSlug(slug);
        }
      },
      {
        root: scrollParent as Element,
        rootMargin: '0px 0px -65% 0px',
        threshold: 0,
      },
    );
    heads.forEach((el) => {
      if (slugByElement.has(el)) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [markdown, containerSelector]);

  const onClick = (slug: string, text: string) => {
    const container = document.querySelector(containerSelector);
    if (!container) return;
    const root = container.querySelector('.ProseMirror');
    if (!root) return;
    // Поиск по точному textContent (не нужно инжектить ids в DOM)
    const heads = root.querySelectorAll<HTMLElement>('h1, h2, h3');
    const target = Array.from(heads).find((h) => (h.textContent ?? '').trim() === text);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSlug(slug);
  };

  if (items.length === 0) {
    return (
      <p className="text-xs text-ink-faint_l dark:text-ink-faint_d py-2">
        В документе пока нет заголовков. Добавь <code>#</code>, <code>##</code> или <code>###</code>.
      </p>
    );
  }

  return (
    <nav aria-label="Оглавление" className="text-sm">
      <ul className="space-y-0.5">
        {items.map((it, i) => {
          const isActive = activeSlug === it.slug;
          const indent = (it.level - 1) * 12;
          return (
            <li key={`${it.slug}-${i}`}>
              <button
                type="button"
                onClick={() => onClick(it.slug, it.text)}
                style={{ paddingLeft: `${indent + 8}px` }}
                className={`w-full text-left py-1 pr-2 rounded text-[13px] leading-snug border-l-2 transition ${
                  isActive
                    ? 'border-accent text-accent bg-accent/8 font-medium'
                    : 'border-transparent text-ink-muted_l dark:text-ink-muted_d hover:text-ink-light dark:hover:text-ink-dark hover:bg-surface3-light dark:hover:bg-surface3-dark'
                } ${it.level === 1 ? 'font-semibold' : ''}`}
                title={it.text}
              >
                <span className="line-clamp-2">{it.text}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
